import { Component, Inject, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { MatDialog, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatTableDataSource } from '@angular/material/table';
import { ApiService } from 'src/app/services/api.service';
import { DataService } from 'src/app/services/data.service';
import { AuthService } from 'src/app/services/auth.service';
import { PopupService } from 'src/app/services/popup.service';
import { firstValueFrom } from 'rxjs';

interface WeatherCostResponse {
  weather_ex_cost: number;
  weather_ex_point?: number;
  [key: string]: any;
}
import {
  feedsConfig,
  SENTINEL_AGREEMENT_FEED_IDS,
} from 'src/app/services/constants';
import {
  FeedGeojsonUploadDialogComponent,
  FeedGeojsonUploadResult,
} from '../../leaflet-task/feed-geojson-upload-dialog/feed-geojson-upload-dialog.component';
import { FeedSentinelAgreementDialogComponent } from '../../leaflet-task/feed-sentinel-agreement-dialog/feed-sentinel-agreement-dialog.component';

@Component({
  selector: 'app-catalogs-popup',
  templateUrl: './catalogs-popup.component.html',
  styleUrls: ['./catalogs-popup.component.scss'],
})
export class CatalogsPopupComponent implements OnInit {
  catlogData: any[] = [];
  informationServices: any[] = [];
  displayedColumns: string[] = ['name', 'price'];
  load: boolean = false;
  loadMessage: string;
  dataSource = new MatTableDataSource<any>();
  darkTheme: boolean;
  tooltipImagePath: string;
  activeUser: any;
  error: boolean;
  sensorId:string;
  savedSelections:[];
  checkedItems: any[] = [];
  weatherCostAmount: number;
  
  constructor(
    private catlogRef: MatDialogRef<CatalogsPopupComponent>,
    @Inject(MAT_DIALOG_DATA)
    public data: any,
    private apiService: ApiService,
    private dataService: DataService,
    private authService: AuthService,
    private popupService: PopupService,
    private dialog: MatDialog,
  ) {
    let isOpenWater = this.data.isOpenWater ? true : false;
    if (this.authService.currentUser?.user_plan === 1) {
      this.loadMessage = 'Sorry, no information feeds found';
      return;
    }
    this.apiService.getInformationServices(this.data.sensorTypes,this.data.taskId,isOpenWater).subscribe((result: any) => {
      if (result.msg === 'Success') {
        this.load = false;
        this.informationServices = result.informationServices;
        const isDisabled =
        this.data.status != 'approved' 
        this.informationServices = result.informationServices.map(item => ({
        ...item,
        sensorId: this.data.sensorId,
        enabled : !isDisabled
      }));
      const feedIds = (this.data.feedData || [])
        .filter(f => f.sensorId === this.data.sensorId) 
        .map(f => f._id);

        this.informationServices = this.informationServices.map(item => ({
        ...item,
        isChecked: item.isChecked || (feedIds.includes(item._id) && item.sensorId === this.data.sensorId)
      }));
        const storageKey = `catalogData_task_${this.data.taskId}_sensor_${this.data.sensorId}`;
        const savedCatalog = localStorage.getItem(storageKey);

        if (savedCatalog) {
          const stored = JSON.parse(savedCatalog);
          // Merge stored isChecked states into API result
          this.informationServices = this.informationServices.map(item => {
            const match = stored.find(s => s._id === item._id);
            return match ? {
              ...item,
              isChecked: match.isChecked,
              legalMiningAreaGeojsonFileName: match.legalMiningAreaGeojsonFileName,
              legalMiningAreaGeojson: match.legalMiningAreaGeojson,
            } : item;
          });
        }
        this.dataSource = new MatTableDataSource<any>(this.informationServices);
      } else {
        this.loadMessage = 'Sorry, no information feeds found';
      }
    });

    this.dataService.darkTheme.subscribe((newValue) => {
      this.darkTheme = newValue;
      this.tooltipImagePath = this.darkTheme ? '../../../assets/images/union-dark.svg' : '../../../assets/images/union.svg';
    });
  }

  ngOnInit(): void {
    this.load = true;
    this.loadMessage = 'Getting information feeds...';
    this.authService.user.subscribe((user) => (this.activeUser = user));
  }

  onClick(click): void {
    if (click === 'yes') {
      if (this.catlogData.length > 0) {
        if (this.data?.isOpenWater && (this.catlogData[0]?.taskingArea != 'water' && this.catlogData[0]?.taskingArea != 'both')) {
          this.error = true
        } else if (!this.data?.isOpenWater &&(this.catlogData[0]?.taskingArea != 'land' && this.catlogData[0]?.taskingArea != 'both')) {
        this.error = true
      } else {
        const storageKey = `catalogData_task_${this.data.taskId}_sensor_${this.data.sensorId}`;
        localStorage.setItem(storageKey, JSON.stringify(this.catlogData));
        this.catlogRef.close(this.catlogData);
      }
      } else {
        this.catlogRef.close(this.catlogData);
      }
    } else if (click === 'no') {
      this.catlogRef.close(click);
    }
  }

  async onItemChange(item: any): Promise<void> {
    this.error = false;

    const shouldCheck = !item.isChecked;
    // Reflect immediate user intent so async popup decisions can reliably revert state.
    item.isChecked = shouldCheck;

    if (!shouldCheck) {
      this.syncSelection(item, false);
      return;
    }

    if (!this.isAoiValid(item)) {
      this.error = true;
      this.syncSelection(item, false);
      return;
    }

    if (item?.weatherIncluded) {
      if (this.isToiBeyondSevenDaysFromSubmission()) {
        await this.showToiWarningPopup();
        this.syncSelection(item, false);
        return;
      }

      const proceedWithWeatherCost = await this.showWeatherCostPopup(item);
      if (!proceedWithWeatherCost) {
        this.syncSelection(item, false);
        return;
      }
    }

    this.syncSelection(item, true);
  }

  private syncSelection(item: any, isChecked: boolean): void {
    item.isChecked = isChecked;

    if (isChecked) {
      if (!this.error && this.isSentinelAgreementFeed(item)) {
        this.openSentinelAgreementFeedUploadFlow(item);
        return;
      }
      if (!this.checkedItems.find((checkedItem) => checkedItem._id === item._id)) {
        this.checkedItems.push(item);
      }
    } else {
      this.checkedItems = this.checkedItems.filter((checkedItem) => checkedItem._id !== item._id);
    }
    this.syncCatalogSelection(item);
  }

  private openSentinelAgreementFeedUploadFlow(item: any): void {
    if (this.getTaskSensorCount() <= 1 && this.isCurrentSensorMsi()) {
      const agreementDialogRef = this.dialog.open(
        FeedSentinelAgreementDialogComponent,
        {
          backdropClass: 'blurr',
          disableClose: true,
          width: '40%',
          data: {
            message: this.getSentinelAgreementMessage(),
          },
        }
      );

      agreementDialogRef.afterClosed().subscribe((result) => {
        if (result === 'agree') {
          this.openGeojsonUploadDialog(item);
        } else {
          this.revertFeedSelection(item);
        }
      });
      return;
    }

    this.openGeojsonUploadDialog(item);
  }

  private getSentinelAgreementMessage(): string {
    if (this.data?.changeDetectionAgreementMessage) {
      return this.data.changeDetectionAgreementMessage.replace(
        'Do you want to proceed?',
        'Do you want to continue?'
      );
    }

    const user = this.data?.currentUser || this.activeUser || this.authService.currentUser;
    if (user?.obfuscated) {
      return 'The selected feed requires both SAR and MSI data. Do you want to continue?';
    }

    return 'The selected feed requires both Sentinel-1 (SAR) and Sentinel-2 (MSI) data. Do you want to continue?';
  }

  private isCurrentSensorMsi(): boolean {
    const currentSensor = this.data?.sensorTypes?.[0];
    const sensorType = String(
      currentSensor?.sensor || currentSensor?.name || ''
    ).trim().toLowerCase();

    return sensorType === 'msi';
  }

  private openGeojsonUploadDialog(item: any): void {
    const uploadDialogRef = this.dialog.open(FeedGeojsonUploadDialogComponent, {
      backdropClass: 'blurr',
      disableClose: true,
      width: '42%',
      data: {
        target: this.data.target,
        taskId: this.data.taskId,
        sensorId: this.data.sensorId,
        customerId: this.activeUser?.id || this.authService.currentUser?.id,
      },
    });

    uploadDialogRef.afterClosed().subscribe((result: FeedGeojsonUploadResult | null) => {
      if (!result) {
        this.revertFeedSelection(item);
        return;
      }

      item.legalMiningAreaGeojsonFileName = result.fileName;
      item.legalMiningAreaGeojson = JSON.parse(result.content);
      if (!this.checkedItems.find(i => i._id === item._id)) {
        this.checkedItems.push(item);
      }
      this.syncCatalogSelection(item);
    });
  }

  private revertFeedSelection(item: any): void {
    item.isChecked = false;
    delete item.legalMiningAreaGeojsonFileName;
    delete item.legalMiningAreaGeojson;
    this.checkedItems = this.checkedItems.filter(i => i._id !== item._id);
    this.syncCatalogSelection(item);
  }

  private syncCatalogSelection(item: any): void {
    this.informationServices = this.informationServices.map(i =>
      i._id === item._id ? { ...item } : i
    );
    this.dataSource = new MatTableDataSource<any>(this.informationServices);
    this.catlogData = [...this.informationServices];
  }

  private isAoiValid(item: any): boolean {
    if (this.data?.isOpenWater) {
      return item.taskingArea === 'water' || item.taskingArea === 'both';
    }

    return item.taskingArea === 'land' || item.taskingArea === 'both';
  }

  private isToiBeyondSevenDaysFromSubmission(): boolean {
    const submissionDate = new Date(this.data?.submissionDate);
    const toiEndDate = new Date(this.data?.closeWindow);

    if (Number.isNaN(submissionDate.getTime()) || Number.isNaN(toiEndDate.getTime())) {
      return false;
    }

    const dayDifference = (toiEndDate.getTime() - submissionDate.getTime()) / (1000 * 60 * 60 * 24);
    return dayDifference > 7;
  }

  private async showWeatherCostPopup(item: any): Promise<boolean> {
    // Fetch weather cost amount from API
    const weatherCharge = await this.getWeatherCost(item);

    const selectedCharge = this.activeUser?.pointsEligible
      ? weatherCharge?.weather_ex_point
      : weatherCharge?.weather_ex_cost;

    // If weather cost is zero, no charge applies — skip the popup and proceed
    if (selectedCharge === 0) return true;

    // Build message with weather cost amount (or fallback to generic message if fetch fails)
    let message: string;
    if (selectedCharge !== null && selectedCharge !== undefined && selectedCharge > 0 && weatherCharge) {
      const weatherCostLabel = this.formatWeatherCost(weatherCharge);
      message = `The selected feed requires weather data costing ${weatherCostLabel} (non-refundable). The platform will add the weather cost to the feed cost if you continue to order the feed.`;
    } else {
      message = 'The selected feed requires weather data. The platform will add the weather cost (non-refundable) to the feed cost if you continue to order the feed.';
    }
    
    const dialogRef = this.popupService.openAlert(
      message,
      'warning',
      [
        { text: 'No', value: 'no' },
        { text: 'Yes', value: 'yes', primary: true },
      ],
      '50%',
      { title: 'Confirmation', darkTheme: this.darkTheme }
    );

    const result = await firstValueFrom(dialogRef.afterClosed());
    return result === 'yes';
  }

  private async getWeatherCost(item: any): Promise<WeatherCostResponse | null> {
    try {
      // Prepare payload for weather cost API
      const payload = {
      target: this.data.coordinates,
      feedId: item._id,
      taskId: this.data.taskId,
      sensorId: this.data.sensorId
      };

      // Call the weather cost API through ApiService
      const response = await firstValueFrom(
        this.apiService.getWeatherCost(payload)
      ) as WeatherCostResponse;

      // Extract weather cost and points from response
      if (response) {
        const weatherCost = Number(response?.weather_ex_cost);
        const weatherPoints = Number(response?.weather_ex_point);

        return {
          weather_ex_cost: Number.isFinite(weatherCost) ? weatherCost : 0,
          weather_ex_point: Number.isFinite(weatherPoints) ? weatherPoints : 0,
        };
      }
      
      return null;
    } catch (error) {
      return null; // Return null on error to use fallback message
    }
  }

  private formatWeatherCost(weatherCharge: WeatherCostResponse): string {
    if (this.activeUser?.pointsEligible) {
      const weatherPoints = Number(weatherCharge?.weather_ex_point ?? 0);
      return `${weatherPoints} Points`;
    }

    const weatherCost = Number(weatherCharge?.weather_ex_cost ?? 0);
    return `US$ ${weatherCost.toFixed(2)}`;
  }


  private async showToiWarningPopup(): Promise<void> {
    const message = 'The weather data is only available for the next seven days. The current task with the time of interest (TOI) exceeding the weather data availability could not be accepted at this time. If you would like to proceed, please cancel and re-task with the TOI within the next seven days.';
    const dialogRef = this.popupService.openAlert(message, 'warning', [{ text: 'Ok, Thanks', value: 'ok', primary: true }], '55%');
    await firstValueFrom(dialogRef.afterClosed());
  }


  private isSentinelAgreementFeed(item: any): boolean {
    const feedId = item?._id;
    if (!feedId || !SENTINEL_AGREEMENT_FEED_IDS.includes(feedId)) {
      return false;
    }

    const configuredFeed = (feedsConfig as Record<string, any>)[feedId];

    return (
      configuredFeed?.requiresSentinelMsiSarAgreement === true &&
      configuredFeed?.type === 'Mining changes monitoring'
    );
  }

  private getTaskSensorCount(): number {
    return this.data?.taskSensorCount || this.data?.sensorTypes?.length || 1;
  }

  getIsToolTip(row: { description: string; f1Score?: number }): string {
    let tooltip = row.description;
    return tooltip;
  }
}
